import { useToast } from "../store/ToastProvider";
import { FlagColor, FlagStatus } from "../enums/Status";
import {
  FLAG_COLOR_TO_STATUS,
  FLAG_KEY_TO_STATUS,
  FLAG_STATUS_TO_FLAG_KEY,
} from "../components/visuals/StatusFlag";
import { Filter } from "../types/PageFilterTypes";
import { usePost } from "./usePost";
import {
  SubjectType,
  TaskSubjectResponse,
  UpdateFlagParams,
} from "../models/workflowModels";

type HandleFlagChange = (flagOption: Filter, id?: string | number) => void;

export const useHandleFlagChange = (
  subjectType: SubjectType
): {
  handleFlagChange: HandleFlagChange;
} => {
  const { addToast } = useToast();
  const { sendData: flagSubject } = usePost<
    null,
    { subject_ids: string[]; subject_type: SubjectType }
  >();
  const { sendData: getSubjectsFlags } = usePost<
    TaskSubjectResponse[],
    { subjects: string[] }
  >();
  const { sendData: updateFlag } = usePost<null, UpdateFlagParams>();

  const handleFlagChange: HandleFlagChange = async (
    flagOption: Filter
  ): Promise<void> => {
    const initialTasks: TaskSubjectResponse[] | null = await getSubjectsFlags(
      "GET_TASKS_SUBJECTS",
      {
        subjects: [flagOption.selectorName as string],
      }
    );
    const initialFlags: TaskSubjectResponse[] = initialTasks
      ? initialTasks.filter((task) => task.is_flag)
      : [];

    if (flagOption.key === FlagColor.WHITE) return;

    if (!initialFlags.length) {
      createFlag(flagOption);
      return;
    }

    const initialStatus: FlagStatus =
      FLAG_KEY_TO_STATUS[initialFlags[0].status];
    const flagStatus: FlagStatus =
      FLAG_COLOR_TO_STATUS[flagOption.key as FlagColor];

    if (initialStatus !== flagStatus) {
      createOrUpdateFlag(flagOption, initialStatus, flagStatus);
    }
  };

  const createFlag = (flagOption: Filter): void => {
    const flagKey = flagOption.key as FlagColor;
    const selectorValue = flagOption.selectorName as string;
    flagSubject("CREATE_FLAG", {
      subject_ids: [selectorValue],
      subject_type: subjectType,
    });

    addToast(`Flag changed to ${FLAG_COLOR_TO_STATUS[flagKey]}`, "info");
  };

  const createOrUpdateFlag = (
    flagOption: Filter,
    initialStatus: FlagStatus,
    flagStatus: FlagStatus
  ): void => {
    if (initialStatus === FlagStatus.RESOLVED) {
      createFlag(flagOption);
      return;
    }
    updateFlag("UPDATE_FLAG", {
      subject_type: subjectType,
      subject: {
        subject_id: flagOption.selectorName as string,
        status: FLAG_STATUS_TO_FLAG_KEY[flagStatus],
      },
    });
    addToast(
      `Flag changed to ${FLAG_COLOR_TO_STATUS[flagOption.key as FlagColor]}`,
      "info"
    );
  };

  return { handleFlagChange };
};
